import { Box, FormLabel, MenuItem, Select } from '@mui/material'
import React, { useState } from 'react'
import GenericButton from '../../../components/GenericButton'
import { ButtonType } from '../../../components/GenericButton/styles'
import Dropdown from '../../../components/Dropdown'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { ReactComponent as ExportIcon } from '../../../assets/Export-icon.svg'
import { DistributionManager } from '../../../APIManager'
import { useQuery } from '@tanstack/react-query'
import { grey } from '../../../colors'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from '../../../components/Notifications'

export const Export: React.FC = () => {
  const [selectedExportFormat, setSelectedExportFormat] = useState<'PDF' | 'CSV'>('PDF')
  const [searchParams] = useSearchParams()
  const { id: instructorId } = useParams()
  const termId = searchParams.get('termId') ?? ''

  const { isLoading: isPDFLoading, isFetching: isPdpPending, refetch: ExportPDF } = useQuery({
    queryKey: ['instructorExportPDF', termId],
    retry: 0,
    queryFn: async () => {
      try {
        const result = instructorId
          ? await DistributionManager.exportPDFByInstructorId(instructorId, termId)
          : await DistributionManager.exportPdf(termId)
        return result
      } catch (error: any) {
        notification('Something went wrong while downloading the file', { variant: 'error' })
      }
    },
    enabled: false
  })

  const { isLoading: isCSVLoading, isFetching: isCSVPending, refetch: ExportCSV } = useQuery({
    queryKey: ['instructorExportCSV', termId],
    retry: 0,
    queryFn: async () => {
      try {
        const result = instructorId
          ? await DistributionManager.exportCSVByInstructorId(instructorId, termId)
          : await DistributionManager.exportCSV(termId)
        return result
      } catch (error: any) {
        notification('Something went wrong while downloading the file', { variant: 'error' })
      }
    },
    enabled: false
  })

  const downloadFile = (data: any): void => {
    const contentDisposition = data?.headers?.['content-disposition'] ?? data?.headers?.contentDisposition
    const fileName = contentDisposition?.match(/filename="?(.*?)"?$/)?.[1] ?? 'Export.zip'
    const blob = new Blob([data?.data], { type: 'text/plain' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = fileName ?? 'Export.zip'
    link.click()
    URL.revokeObjectURL(url)
    notification('File successfully downloaded', { variant: 'success', subTitle: fileName })
  }

  const handleExport = async (): Promise<void> => {
    try {
      const exportFunction = selectedExportFormat === 'PDF' ? ExportPDF : ExportCSV
      const result = await exportFunction()
      result.data && downloadFile(result?.data)
    } catch (error) {
      console.error('Export failed:', error)
      throw error
    }
  }

  const ExportContent = (): JSX.Element => {
    return (
      <>
        <FormLabel sx={{ fontWeight: 500, color: grey[950] }} >File Type</FormLabel>
        <Select
          fullWidth
          value={selectedExportFormat}
          sx={{ mt: 1.5 }}
          onChange={(e) => { setSelectedExportFormat(e.target.value as 'PDF' | 'CSV') }}
          IconComponent={KeyboardArrowDownRoundedIcon}
        >
          <MenuItem value="CSV">CSV</MenuItem>
          <MenuItem value="PDF">PDF</MenuItem>
        </Select>
        <Box display={'flex'} justifyContent={'flex-end'} mt={6}>
          <GenericButton loading={isPDFLoading || isCSVLoading || isCSVPending || isPdpPending} buttonType={ButtonType.Primary} label='Export' onClick={() => { void handleExport() }} />
        </Box>
      </>
    )
  }

  return (
    <Dropdown label='Export'
      menuPaperStyles={{ width: '340px' }}
      extraButtonProps={{ endIcon: <ExportIcon /> }}
      menuItems={<ExportContent />} />
  )
}
